import { useEffect, useState } from "react";
import { Mail, Sparkles, Music, TrendingUp, Users, ShieldCheck, Coins } from "lucide-react";
import { toast } from "sonner";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { fmtNumber } from "@/lib/format";
import KPI from "./KPI";

export default function OverviewTab() {
  const auth = useAdminAuth();
  const [metrics, setMetrics] = useState(null);
  const [signups, setSignups] = useState([]);

  useEffect(() => {
    Promise.all([
      auth.apiGet("/admin/metrics"),
      auth.apiGet("/admin/recent-signups?limit=15"),
    ]).then(([m, s]) => {
      setMetrics(m.data);
      setSignups(s.data);
    }).catch(() => toast.error("No se pudieron cargar las métricas"));
  }, [auth]);

  return (
    <div data-testid="tab-overview" className="space-y-6">
      <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        <KPI testid="kpi-beta"      icon={Mail}        k={fmtNumber(metrics?.beta_signups ?? 0)}     v="beta signups" highlight />
        <KPI testid="kpi-pro"       icon={Sparkles}    k={fmtNumber(metrics?.pro_users ?? 0)}        v="usuarios PRO activos" />
        <KPI testid="kpi-tracks"    icon={Music}       k={fmtNumber(metrics?.tracks ?? 0)}           v="obras musicales" />
        <KPI testid="kpi-artists"   icon={Music}       k={fmtNumber(metrics?.artists ?? 0)}          v="artistas" />
        <KPI testid="kpi-promoted"  icon={TrendingUp}  k={fmtNumber(metrics?.tracks_promoted_horizonte ?? 0)} v="tracks en Horizonte" />
        <KPI testid="kpi-referrals" icon={Users}       k={fmtNumber(metrics?.referrals_total ?? 0)}  v="referidos activos" />
        <KPI testid="kpi-codes"     icon={ShieldCheck} k={fmtNumber(metrics?.referral_codes ?? 0)}   v="códigos OVX generados" />
        <KPI testid="kpi-sparks"    icon={Coins}       k={fmtNumber(metrics?.spark_total_circulating ?? 0)} v="Sparks en circulación" />
      </section>

      <section className="border hairline bg-cream-card rounded-2xl p-3 md:p-5" data-testid="overview-signups">
        <div className="flex items-center justify-between mb-3 px-2">
          <h3 className="font-serif text-2xl tracking-tight">Últimos beta signups</h3>
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid">
            {signups.length} más recientes
          </span>
        </div>
        {signups.length === 0 ? (
          <p className="text-sm text-ink-mid text-center py-8" data-testid="signups-empty">Aún no hay registros en lista de espera.</p>
        ) : (
          <>
            <div className="grid grid-cols-12 gap-3 px-3 pb-2 border-b hairline font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid">
              <div className="col-span-5">email</div>
              <div className="col-span-3">nombre</div>
              <div className="col-span-2">interés</div>
              <div className="col-span-2 text-right">fecha</div>
            </div>
            {signups.map((s) => (
              <div key={s.id} data-testid={`signup-${s.id}`} className="grid grid-cols-12 gap-3 px-3 py-3 border-b hairline last:border-b-0 items-center">
                <div className="col-span-5 text-sm truncate font-mono">{s.email}</div>
                <div className="col-span-3 text-sm text-ink-mid truncate">{s.name || "—"}</div>
                <div className="col-span-2 text-xs">
                  <span className="px-2 py-0.5 rounded-full bg-coral/10 text-coral font-mono uppercase tracking-wider text-[10px]">
                    {s.interest}
                  </span>
                </div>
                <div className="col-span-2 text-right text-xs text-ink-mid font-mono">
                  {new Date(s.created_at).toLocaleDateString("es", { day: "2-digit", month: "short" })}
                </div>
              </div>
            ))}
          </>
        )}
      </section>
    </div>
  );
}
